import { baseApi } from 'api/baseApi';
import { blobResponseHandler } from 'store/api/blobResponseHandler';

export interface Contact {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  user: any; // TODO
  salutation: string;
  phone?: string;
  mobile?: string;
  fax?: string;
  switchboard?: string;
  company?: string;
  street?: string;
  location?: string;
  country?: string;
  position?: string;
  supervisor?: string;
  division?: string;
  image?: any; // TODO
}

export type ContactMutation = { contact: Contact; image?: File };

const prepareContactMutation = (contact: Contact, image?: File): FormData => {
  const formData = new FormData();
  const contactJson = new Blob([JSON.stringify({ ...contact })], { type: 'application/json' });
  formData.append('contact', contactJson);
  if (image) {
    formData.append('image', image);
  }
  return formData;
};

const extendedApi = baseApi.enhanceEndpoints({ addTagTypes: ['Contact'] }).injectEndpoints({
  endpoints: build => ({
    getContacts: build.query<Contact[], void>({
      query: () => `/api/rest/contacts`,
      providesTags: result =>
        result ? [...result.map(({ id }) => ({ type: 'Contact' as const, id })), 'Contact'] : ['Contact'],
    }),
    getContactsBySpaceId: build.query<Contact[], number>({
      query: spaceId => `/api/rest/contacts?spaceId=${spaceId}`,
      providesTags: result =>
        result ? [...result.map(({ id }) => ({ type: 'Contact' as const, id })), 'Contact'] : ['Contact'],
    }),
    getContact: build.query<Contact, number>({
      query: id => `/api/rest/contacts/${id}`,
      providesTags: (result, error, id) => [{ type: 'Contact', id }],
    }),
    getContactByUserId: build.query<Contact, number>({
      query: userId => `/api/rest/users/${userId}/contact`,
      providesTags: result => (result ? [{ type: 'Contact', id: result.id }] : []),
    }),
    getMyContact: build.query<Contact, void>({
      query: () => `/api/rest/me/contact`,
      providesTags: result => (result ? [{ type: 'Contact', id: result.id }] : []),
    }),
    createContact: build.mutation<Contact, ContactMutation>({
      query: ({ contact, image }) => {
        const formData = prepareContactMutation(contact, image);
        return {
          url: `/api/rest/contacts`,
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: (result, error, { contact: { id } }) => [{ type: 'Contact', id }, 'Contact'],
    }),
    updateContact: build.mutation<Contact, ContactMutation>({
      query: ({ contact, image }) => {
        const formData = prepareContactMutation(contact, image);
        return {
          url: `/api/rest/contacts/${contact.id}`,
          method: 'PUT',
          body: formData,
        };
      },
      invalidatesTags: (result, error, { contact: { id } }) => [{ type: 'Contact', id }, 'Contact'],
    }),
    updateMyContact: build.mutation<Contact, ContactMutation>({
      query: ({ contact, image }) => {
        const formData = prepareContactMutation(contact, image);
        return {
          url: `/api/rest/me/contact`,
          method: 'PUT',
          body: formData,
        };
      },
      invalidatesTags: (result, error, { contact: { id } }) => [{ type: 'Contact', id }, 'Contact'],
    }),
    deleteContact: build.mutation<void, number>({
      query: id => ({
        url: `/api/rest/contacts/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Contact'],
    }),
    importContactsFromCsv: build.mutation<Contact[], File>({
      query: file => {
        const formData = new FormData();
        formData.append('csv', file);
        return {
          url: `/api/rest/contacts`,
          method: 'PUT',
          body: formData,
        };
      },
      invalidatesTags: result =>
        result ? [...result.map(({ id }) => ({ type: 'Contact' as const, id })), 'Contact'] : ['Contact'],
    }),
    downloadContactVcard: build.mutation<Blob, number>({
      query: id => ({
        url: `/api/rest/contacts/download/${id}`,
        method: 'GET',
        responseHandler: blobResponseHandler,
      }),
    }),
  }),
});

export const {
  useGetContactsQuery,
  useGetContactQuery,
  useGetContactByUserIdQuery,
  useGetMyContactQuery,
  useCreateContactMutation,
  useUpdateContactMutation,
  useUpdateMyContactMutation,
  useDeleteContactMutation,
  useImportContactsFromCsvMutation,
  useDownloadContactVcardMutation,
} = extendedApi;
